import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Wrench, Building2, DraftingCompass, Paintbrush, Factory } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { AUDIENCES, BUSINESS_MODEL, IMAGES } from "@/data/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const ICONS = { Home: HomeIcon, Wrench, Building2, DraftingCompass, Paintbrush, Factory };

export default function Solutions() {
  usePageMeta(
    "Solutions for Every Project | Chhannulal & Brothers (CLB)",
    "CLB serves homeowners, contractors, builders, architects, designers, plumbers, painters and commercial buyers — retail, wholesale and distribution of trusted building material brands."
  );
  return (
    <main>
      <section className="bg-darknavy pb-20 pt-36 text-cream lg:pb-28 lg:pt-44" data-testid="solutions-hero">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline dark>Who we serve</Overline>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-light tracking-tight sm:text-6xl">Solutions for Every Project</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70">
              From a single bathroom renovation to a multi-unit build, CLB serves both individual customers and professional project requirements — with the same care.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="solutions-audiences">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map((a, i) => {
              const Icon = ICONS[a.icon];
              return (
                <Reveal key={a.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full border border-darknavy/10 bg-white p-8 transition-all duration-500 hover:border-navy hover:shadow-[0_20px_50px_rgba(4,57,94,0.12)]" data-testid={`solutions-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <Icon className="h-7 w-7 text-navy transition-colors duration-300 group-hover:text-aqua" />
                    <h2 className="mt-5 font-display text-xl font-medium text-darknavy">{a.title}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-darknavy/60">{a.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <Overline>Retail · Wholesale · Distribution</Overline>
                <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-4xl">
                  One supplier. <span className="font-medium">Three ways to buy.</span>
                </h2>
                <div className="mt-8 space-y-6">
                  {BUSINESS_MODEL.map((m) => (
                    <div key={m.num} className="flex gap-5 border-b border-darknavy/10 pb-6">
                      <span className="font-display text-2xl font-light text-gold">{m.num}</span>
                      <div>
                        <h3 className="font-display text-lg font-medium text-darknavy">{m.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-darknavy/60">{m.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/contact" data-testid="solutions-contact-cta" className="group mt-10 inline-flex items-center gap-3 bg-navy px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-aqua">
                  Discuss Your Requirement
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <div className="relative">
                  <div className="absolute -bottom-4 -right-4 h-full w-full border border-gold/50" aria-hidden="true" />
                  <div className="relative h-80 overflow-hidden sm:h-[26rem]">
                    <SafeImage src={IMAGES.livingRoom} alt="A finished home interior supplied through CLB's complete home solutions" className="h-full w-full object-cover" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
